/**
 * Allocation conflicts + Vehicle time windows data access (issue #305).
 *
 * Mirrors go-service/internal/api/allocation_conflicts.go and
 * vehicle_windows_handlers.go:
 *
 *   GET  /api/admin/events/{id}/allocation-conflicts
 *   GET  /api/admin/events/{id}/vehicle-windows
 *   PUT  /api/admin/events/{id}/vehicle-windows
 *
 * The conflict read WARNS; it never gates a save. Nothing in this module is
 * allowed to block a mutation on its result — a truck legitimately does two
 * short local jobs in a day, and the Operator overriding the notice is the
 * normal case.
 */
import { apiFetch } from "../../../shared/api-client";

/** One other Booking holding the same Vehicle or Contractor. */
export interface AllocationClash {
  booking_id: string;
  quote_number: string;
  event_name: string | null;
  client_name: string;
  phase: string;
  event_date: string | null;
  event_end_date: string | null;
}

export interface VehicleConflict {
  vehicle_id: string;
  vehicle_name: string;
  clashes: AllocationClash[];
}

export interface ContractorConflict {
  contractor_id: string;
  contractor_name: string;
  clashes: AllocationClash[];
}

export interface AllocationConflicts {
  vehicles: VehicleConflict[];
  contractors: ContractorConflict[];
}

export const NO_ALLOCATION_CONFLICTS: AllocationConflicts = {
  vehicles: [],
  contractors: [],
};

export async function getAllocationConflicts(
  bookingId: string,
  signal?: AbortSignal,
): Promise<AllocationConflicts> {
  const res = await apiFetch<Partial<AllocationConflicts>>(
    `/api/admin/events/${bookingId}/allocation-conflicts`,
    { signal },
  );
  return {
    vehicles: res.vehicles ?? [],
    contractors: res.contractors ?? [],
  };
}

/**
 * One day of one planned Vehicle being out. Null times mean unknown, which
 * the overlap check reads as a whole-day commitment — never as zero.
 */
export interface VehicleWindow {
  vehicle_id: string;
  window_date: string;
  start_time: string | null;
  end_time: string | null;
}

export async function listVehicleWindows(
  bookingId: string,
  signal?: AbortSignal,
): Promise<VehicleWindow[]> {
  const res = await apiFetch<{ windows: VehicleWindow[] }>(
    `/api/admin/events/${bookingId}/vehicle-windows`,
    { signal },
  );
  return res.windows ?? [];
}

/**
 * Retime windows. Per DESIGN-SYSTEM.md there is no save button — the gear editor
 * commits on blur, so this normally carries the one window that changed.
 */
export async function putVehicleWindows(
  bookingId: string,
  windows: VehicleWindow[],
): Promise<VehicleWindow[]> {
  const res = await apiFetch<{ windows: VehicleWindow[] }>(
    `/api/admin/events/${bookingId}/vehicle-windows`,
    { method: "PUT", body: { windows } },
  );
  return res.windows ?? [];
}
